import { AssignmentTurnedIn, Warning } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Stack,
  Tab,
  Tabs,
} from "@mui/material";
import { GridExpandMoreIcon } from "@mui/x-data-grid";
import { ReactNode, SyntheticEvent, useState } from "react";
import { MedicalNotes } from "./components/MedicalNotes";
import { NoteReport } from "./components/NoteReport";
import { Solicitations } from "./components/Solicitations";
import { useEhrRecordsMobileLayout } from "../../../context/EhrRecordsLayoutContext";
import { EhrSummaryMobileSection } from "../../mobile/EhrSummaryMobileSection";
import { EhrMobilePillTabs } from "../../mobile/EhrResponsiveTable";
import { useProcessedDocumentLists } from "./useProcessedDocumentLists";

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ maxHeight: "60vh", pt: 2, overflow: "auto" }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export function Documents({
  medicalNoteData,
  solicitationData,
  resultsData,
  NoteReportData,
  setRefetch,
  title,
  handleOpen,
  setBinaryUrl,
  sign,
}: any) {
  const [selectedTab, setSelectedTab] = useState(0);

  const handleChange = (_event: SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  // Mesma lógica de filtro/processamento — centralizada para o atalho de reemissão
  const {
    processedMedicalNoteData,
    processedSolicitationData,
    processedNoteReportData,
    filteredMedicalNoteCount,
    filteredSolicitationCount,
    filteredNoteReportCount,
  } = useProcessedDocumentLists({
    medicalNoteData,
    solicitationData,
    resultsData,
    NoteReportData,
    sign,
  });

  const isEhrMobile = useEhrRecordsMobileLayout();
  const totalDocumentsCount =
    filteredMedicalNoteCount +
    filteredSolicitationCount +
    filteredNoteReportCount;

  const tabPanels = (
    <>
      {selectedTab === 0 ? (
        <MedicalNotes
          setRefetch={setRefetch}
          sign={sign}
          rowData={processedMedicalNoteData}
          handleOpen={handleOpen}
          setBinaryUrl={setBinaryUrl}
        />
      ) : null}
      {selectedTab === 1 ? (
        <Solicitations
          sign={sign}
          setRefetch={setRefetch}
          handleOpen={handleOpen}
          setBinaryUrl={setBinaryUrl}
          rowData={processedSolicitationData}
          resultsData={resultsData}
        />
      ) : null}
      {selectedTab === 2 ? (
        <NoteReport
          setRefetch={setRefetch}
          sign={sign}
          rowData={processedNoteReportData}
          handleOpen={handleOpen}
          setBinaryUrl={setBinaryUrl}
        />
      ) : null}
    </>
  );

  if (isEhrMobile) {
    return (
      <EhrSummaryMobileSection
        title={sign ? "Documentos assinados" : title ?? "Assinaturas pendentes"}
        subtitle={`${totalDocumentsCount} documento${
          totalDocumentsCount === 1 ? "" : "s"
        }`}
      >
        <EhrMobilePillTabs
          tabs={[
            {
              value: "0",
              label: `Receitas (${filteredMedicalNoteCount || 0})`,
            },
            {
              value: "1",
              label: `Exames (${filteredSolicitationCount || 0})`,
            },
            {
              value: "2",
              label: `Relatórios (${filteredNoteReportCount || 0})`,
            },
          ]}
          value={String(selectedTab)}
          onChange={(value) => setSelectedTab(Number(value))}
        />
        {tabPanels}
      </EhrSummaryMobileSection>
    );
  }

  return (
    <>
      <Accordion sx={{ width: "100%" }} elevation={5}>
        <AccordionSummary
          expandIcon={<GridExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <Stack direction="row" alignItems="center" gap={1}>
            {sign ? (
              <>
                <AssignmentTurnedIn sx={{ color: "success600.main" }} />
                Documentos assinados
              </>
            ) : (
              <>
                <Warning sx={{ color: "#FBBB3C" }} />
                Assinaturas pendentes
              </>
            )}
            <Avatar
              sx={{
                width: 26,
                height: 26,
                fontSize: "14px",
                fontWeight: 600,
                backgroundColor: "primary100.main",
                color: "primary1000.main",
              }}
            >
              {filteredMedicalNoteCount +
                filteredSolicitationCount +
                filteredNoteReportCount || "0"}
            </Avatar>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={selectedTab}
              onChange={handleChange}
              aria-label="basic tabs example"
              variant="scrollable"
            >
              <Tab
                label={
                  <Box style={{ display: "flex", alignItems: "center" }}>
                    Receitas médicas
                    <Avatar
                      sx={{
                        width: 26,
                        marginLeft: 1,
                        height: 26,
                        fontSize: "14px",
                        fontWeight: 600,
                        backgroundColor: "primary100.main",
                        color: "primary1000.main",
                      }}
                    >
                      {filteredMedicalNoteCount || "0"}
                    </Avatar>
                  </Box>
                }
                {...a11yProps(0)}
              />
              <Tab
                label={
                  <Box style={{ display: "flex", alignItems: "center" }}>
                    Solicitações de exame
                    <Avatar
                      sx={{
                        width: 26,
                        marginLeft: 1,
                        height: 26,
                        fontSize: "14px",
                        fontWeight: 600,
                        backgroundColor: "primary100.main",
                        color: "primary1000.main",
                      }}
                    >
                      {filteredSolicitationCount || "0"}
                    </Avatar>
                  </Box>
                }
                {...a11yProps(1)}
              />
              <Tab
                label={
                  <Box style={{ display: "flex", alignItems: "center" }}>
                    Relatórios e atestados
                    <Avatar
                      sx={{
                        width: 26,
                        marginLeft: 1,
                        height: 26,
                        fontSize: "14px",
                        fontWeight: 600,
                        backgroundColor: "primary100.main",
                        color: "primary1000.main",
                      }}
                    >
                      {filteredNoteReportCount || "0"}
                    </Avatar>
                  </Box>
                }
                {...a11yProps(2)}
              />
            </Tabs>
          </Box>

          <CustomTabPanel value={selectedTab} index={0}>
            <MedicalNotes
              setRefetch={setRefetch}
              sign={sign}
              rowData={processedMedicalNoteData}
              handleOpen={handleOpen}
              setBinaryUrl={setBinaryUrl}
            />
          </CustomTabPanel>
          <CustomTabPanel value={selectedTab} index={1}>
            <Solicitations
              sign={sign}
              setRefetch={setRefetch}
              handleOpen={handleOpen}
              setBinaryUrl={setBinaryUrl}
              rowData={processedSolicitationData}
              resultsData={resultsData}
            />
          </CustomTabPanel>
          <CustomTabPanel value={selectedTab} index={2}>
            <NoteReport
              setRefetch={setRefetch}
              sign={sign}
              rowData={processedNoteReportData}
              handleOpen={handleOpen}
              setBinaryUrl={setBinaryUrl}
            />
          </CustomTabPanel>
        </AccordionDetails>
      </Accordion>
    </>
  );
}
